import * as yup from "yup";

// Shared payout configuration field definitions.
// Used by both the per-station Configurations.jsx (chargingStation) and the
// payouts Configurations tab modal so the two stay in sync.

// Groups used to organize the fields in the read-only summary view.
export const PAYOUT_CONFIG_GROUPS = [
  "Pricing",
  "Hosting & Urzza",
  "Limits & Deductions",
];

export const PAYOUT_CONFIG_FIELDS = [
  {
    name: "ElectricityUnitPrice",
    label: "Electricity Unit Price (₹)",
    shortLabel: "Electricity Unit Price",
    unit: "₹",
    group: "Pricing",
    placeholder: "Enter price per unit",
    type: "number",
    step: "0.01",
    required: true,
  },
  {
    name: "AgreedPrice",
    label: "Agreed Price (₹)",
    shortLabel: "Agreed Price",
    unit: "₹",
    group: "Pricing",
    placeholder: "Enter agreed price",
    type: "number",
    step: "0.01",
    required: true,
  },
  {
    name: "DemandCharge",
    label: "Demand Charge (₹)",
    shortLabel: "Demand Charge",
    unit: "₹",
    group: "Pricing",
    placeholder: "Enter demand charge",
    type: "number",
    step: "0.01",
    required: true,
  },
  {
    name: "NetworkCharge",
    label: "Network Charge (₹)",
    shortLabel: "Network Charge",
    unit: "₹",
    group: "Pricing",
    placeholder: "Enter network charge",
    type: "number",
    step: "0.01",
    required: true,
  },
  {
    name: "HostingChargePerUnit",
    label: "Hosting Charge Per Unit (₹)",
    shortLabel: "Hosting / Unit",
    unit: "₹",
    group: "Hosting & Urzza",
    placeholder: "Enter hosting charge per unit",
    type: "number",
    step: "0.01",
    required: true,
  },
  {
    name: "UrzzaChargePerUnit",
    label: "Urzza Charge Per Unit (₹)",
    shortLabel: "Urzza / Unit",
    unit: "₹",
    group: "Hosting & Urzza",
    placeholder: "Enter Urzza charge",
    type: "number",
    step: "0.01",
    required: true,
  },
  {
    name: "UrzzaFixedCharge",
    label: "Urzza Fixed Charge (₹)",
    shortLabel: "Urzza Fixed Charge",
    unit: "₹",
    group: "Hosting & Urzza",
    placeholder: "Enter Urzza fixed charge",
    type: "number",
    step: "0.01",
    required: true,
  },
  {
    name: "UrzzaChargeInPercent",
    label: "Urzza Charge In Percent (%)",
    shortLabel: "Urzza Charge",
    unit: "%",
    group: "Hosting & Urzza",
    placeholder: "Enter Urzza charge in percent",
    type: "number",
    step: "0.01",
    required: true,
  },
  {
    name: "MinAmount",
    label: "Minimum Amount (₹)",
    shortLabel: "Min Amount",
    unit: "₹",
    group: "Limits & Deductions",
    placeholder: "Enter minimum amount",
    type: "number",
    step: "0.01",
    required: true,
  },
  {
    name: "MinUnits",
    label: "Minimum Units",
    shortLabel: "Min Units",
    unit: "",
    group: "Limits & Deductions",
    placeholder: "Enter minimum units",
    type: "number",
    step: "0.01",
    required: true,
  },
  {
    name: "ExternalFixedIncome",
    label: "External Fixed Income (₹)",
    shortLabel: "External Fixed Income",
    unit: "₹",
    group: "Limits & Deductions",
    placeholder: "Enter external fixed income",
    type: "number",
    step: "0.01",
    required: true,
  },
  {
    name: "AdditionalDeductionsPerUnit",
    label: "Additional Deductions Per Unit (₹)",
    shortLabel: "Additional Deductions / Unit",
    unit: "₹",
    group: "Limits & Deductions",
    placeholder: "Enter additional deductions per unit",
    type: "number",
    step: "0.01",
    required: true,
  },
];

export const PAYOUT_CONFIG_FIELD_NAMES = PAYOUT_CONFIG_FIELDS.map(
  (f) => f.name,
);

export const PAYOUT_CONFIG_DEFAULTS = PAYOUT_CONFIG_FIELDS.reduce(
  (acc, f) => {
    acc[f.name] = 0;
    return acc;
  },
  {},
);

export const payoutConfigValidationSchema = yup.object().shape({
  ElectricityUnitPrice: yup
    .number()
    .typeError("Must be a number")
    .min(0, "Must be at least 0")
    .required("Electricity unit price is required"),
  DemandCharge: yup
    .number()
    .typeError("Must be a number")
    .min(0, "Must be at least 0")
    .required("Demand charge is required"),
  NetworkCharge: yup
    .number()
    .typeError("Must be a number")
    .min(0, "Must be at least 0")
    .required("Network charge is required"),
  HostingChargePerUnit: yup
    .number()
    .typeError("Must be a number")
    .min(0, "Must be at least 0")
    .required("Hosting charge is required"),
  UrzzaChargePerUnit: yup
    .number()
    .typeError("Must be a number")
    .min(0, "Must be at least 0")
    .required("Urzza charge is required"),
  MinAmount: yup
    .number()
    .typeError("Must be a number")
    .min(0, "Must be at least 0")
    .required("Minimum amount is required"),
  MinUnits: yup
    .number()
    .typeError("Must be a number")
    .min(0, "Must be at least 0")
    .required("Minimum units is required"),
  AgreedPrice: yup
    .number()
    .typeError("Must be a number")
    .min(0, "Must be at least 0")
    .required("Agreed price is required"),
  UrzzaFixedCharge: yup
    .number()
    .typeError("Must be a number")
    .min(0, "Must be at least 0")
    .required("Urzza fixed charge is required"),
  ExternalFixedIncome: yup
    .number()
    .typeError("Must be a number")
    .min(0, "Must be at least 0")
    .required("External fixed income is required"),
  UrzzaChargeInPercent: yup
    .number()
    .typeError("Must be a number")
    .min(0, "Must be at least 0")
    .required("Urzza charge in percent is required"),
  AdditionalDeductionsPerUnit: yup
    .number()
    .typeError("Must be a number")
    .min(0, "Must be at least 0")
    .required("Additional deductions per unit is required"),
});

// Build a payload object from form data, coercing strings to numbers.
// Month/Year identify the period the configuration applies to, so configs can
// be created/edited for past periods as well as the current one.
export const buildPayoutConfigPayload = (data, stationId, month, year) => {
  const payload = { StationId: stationId };
  PAYOUT_CONFIG_FIELDS.forEach((field) => {
    payload[field.name] = parseFloat(data[field.name]);
  });
  if (month) payload.Month = parseInt(month);
  if (year) payload.Year = parseInt(year);
  return payload;
};

// Reset form values from a fetched config object.
export const payoutConfigFormValues = (config) => {
  if (!config) return { ...PAYOUT_CONFIG_DEFAULTS };
  const values = {};
  PAYOUT_CONFIG_FIELDS.forEach((field) => {
    values[field.name] = config[field.name] ?? 0;
  });
  return values;
};

// Coerce a numeric form field's value into a string suitable for an <Input>.
// Null/undefined/0 all render as "0" so the field never appears empty; any
// other value (e.g. user-typed number) is passed through unchanged.
export const formatPayoutConfigInputValue = (value) =>
  value === 0 || value === null || value === undefined ? "0" : value;
